import { useUserSettings } from "@next-platform/client-common-core";
import type { UiState } from "instantsearch.js";
import qs from "qs";
import { useEffect, useMemo } from "react";
import { useInstantSearch } from "react-instantsearch";

import useLocalizedLocation from "@/hooks/useLocalizedLocation";

import { parseInstantSearchUrlState } from "../utils/parseInstantSearchUrlState";

const TAGS_ATTRIBUTE = "tags";

const resolveParsedIndexUiState = ({ parsedUrlState, searchIndexName }: { parsedUrlState: UiState; searchIndexName: string }) => {
  if (parsedUrlState[searchIndexName]) {
    return parsedUrlState[searchIndexName];
  }

  const parsedIndexNames = Object.keys(parsedUrlState);
  if (parsedIndexNames.length !== 1) {
    return undefined;
  }

  return parsedUrlState[parsedIndexNames[0]];
};

const getUrlTagsFromSearch = ({ search, searchIndexName }: { search: string; searchIndexName: string }) => {
  const parsedUrlState = parseInstantSearchUrlState(qs, search) as UiState;
  const parsedIndexUiState = resolveParsedIndexUiState({ parsedUrlState, searchIndexName });
  const urlTags = (parsedIndexUiState as { refinementList?: { tags?: string[] } } | undefined)?.refinementList?.tags;

  return urlTags ?? [];
};

const areTagRefinementsEqual = ({ currentTags, urlTags }: { currentTags: string[] | undefined; urlTags: string[] }) => {
  return JSON.stringify(currentTags ?? []) === JSON.stringify(urlTags);
};

const useInstantSearchUrlStateSync = ({ searchIndexName }: { searchIndexName: string }) => {
  const { search } = useLocalizedLocation();
  const { setIndexUiState } = useInstantSearch();
  const { setLastTimeFilteredCharacters } = useUserSettings();

  const urlTagsKey = useMemo(() => JSON.stringify(getUrlTagsFromSearch({ search, searchIndexName })), [search, searchIndexName]);

  useEffect(() => {
    const urlTags = JSON.parse(urlTagsKey) as string[];

    if (!urlTags.length) {
      return;
    }

    let didApplyUrlTags = false;

    setIndexUiState((previousIndexUiState) => {
      const currentTags = previousIndexUiState.refinementList?.[TAGS_ATTRIBUTE];
      if (areTagRefinementsEqual({ currentTags, urlTags })) {
        return previousIndexUiState;
      }

      didApplyUrlTags = true;

      return {
        ...previousIndexUiState,
        refinementList: {
          ...previousIndexUiState.refinementList,
          [TAGS_ATTRIBUTE]: urlTags,
        },
      };
    });

    if (didApplyUrlTags) {
      setLastTimeFilteredCharacters(Date.now());
    }
  }, [urlTagsKey, setIndexUiState, setLastTimeFilteredCharacters]);
};

export default useInstantSearchUrlStateSync;
